import { loadGoogleMaps } from '@/utils/loadGoogleMaps';

const normalizeText = (value) =>
  (value == null ? '' : String(value))
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}+/gu, '');

const getComponent = (place, type) => {
  const comps = place?.address_components;
  if (!Array.isArray(comps)) return null;
  return comps.find((c) => Array.isArray(c?.types) && c.types.includes(type)) ?? null;
};

export const parsePlaceAddress = (place) => {
  const route = getComponent(place, 'route')?.long_name ?? '';
  const streetNumber = getComponent(place, 'street_number')?.long_name ?? '';
  const postalCode = getComponent(place, 'postal_code')?.long_name ?? '';

  const provincia =
    getComponent(place, 'administrative_area_level_1')?.long_name ??
    getComponent(place, 'administrative_area_level_1')?.short_name ??
    '';

  const localidad =
    getComponent(place, 'locality')?.long_name ??
    getComponent(place, 'administrative_area_level_2')?.long_name ??
    getComponent(place, 'sublocality')?.long_name ??
    getComponent(place, 'sublocality_level_1')?.long_name ??
    '';

  const direccion = [route, streetNumber].filter(Boolean).join(' ').trim();

  return {
    direccion,
    codigo_postal: postalCode,
    provincia,
    localidad,
  };
};

export const matchProvinciaIdByName = (provincias, provinciaName) => {
  const list = Array.isArray(provincias) ? provincias : [];
  const needle = normalizeText(provinciaName);
  if (!needle) return null;

  const found = list.find((p) => normalizeText(p?.nombre) === needle) ??
    list.find((p) => normalizeText(p?.nombre).includes(needle) || needle.includes(normalizeText(p?.nombre)));

  const id = Number(found?.id);
  return Number.isFinite(id) ? id : null;
};

export const attachPlacesAutocomplete = async ({
  inputEl,
  apiKey,
  onSelect,
  country = 'ar',
} = {}) => {
  if (!inputEl) return () => {};
  if (!apiKey) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[googlePlaces] Missing apiKey; autocomplete not initialized.');
    }
    return () => {};
  }

  await loadGoogleMaps(apiKey);
  if (!globalThis.google?.maps?.places?.Autocomplete) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[googlePlaces] Google Maps Places library not available; check API key, billing, and enabled APIs.');
    }
    return () => {};
  }

  const autocomplete = new globalThis.google.maps.places.Autocomplete(inputEl, {
    types: ['address'],
    componentRestrictions: { country },
    fields: ['address_components', 'formatted_address', 'name'],
  });

  // Compat (algunas versiones ignoran fields en options)
  try {
    if (typeof autocomplete.setFields === 'function') {
      autocomplete.setFields(['address_components', 'formatted_address', 'name']);
    }
  } catch (e) {
    // noop
  }

  const listener = autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    if (typeof onSelect === 'function') onSelect(place);
  });

  return () => {
    try {
      if (listener && typeof listener.remove === 'function') listener.remove();
    } catch (e) {
      // noop
    }
  };
};
